
import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { jsPDF } from 'jspdf';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { 
  Upload, FileText, ArrowLeft, ArrowRight, Download, Trash2, 
  Loader2, Image as ImageIcon, Edit2, Wand2 
} from 'lucide-react';
import ImageEditor from './pdf-converter/ImageEditor';
import PDFSettings, { PDFSettingsState } from './pdf-converter/PDFSettings';
import { processImageForText, OCRResult } from './pdf-converter/OCRProcessor';
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

// Types
interface PDFImage {
  id: string;
  file: File;
  previewUrl: string;
  width: number;
  height: number;
  ocrText?: string;
  ocrStatus?: 'pending' | 'processing' | 'done' | 'failed';
}

const IndependentPDFConverterView = () => {
  const navigate = useNavigate();
  const [images, setImages] = useState<PDFImage[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [editingImageId, setEditingImageId] = useState<string | null>(null);
  const [settings, setSettings] = useState<PDFSettingsState>({
    pageSize: 'a4',
    orientation: 'portrait',
    margin: 10,
    compression: 0.8,
    layout: 'single',
    includeOCR: false
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  // File Handling
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      processFiles(Array.from(e.target.files));
    }
  };

  const processFiles = (files: File[]) => {
    const validImages = files.filter(file => file.type.startsWith('image/'));
    const newImages = validImages.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      previewUrl: URL.createObjectURL(file),
      width: 0, 
      height: 0,
      ocrStatus: 'pending' as const
    }));

    newImages.forEach(img => {
      const i = new Image();
      i.onload = () => {
        setImages(prev => prev.map(p => p.id === img.id ? { ...p, width: i.width, height: i.height } : p));
      };
      i.src = img.previewUrl;
    });

    setImages(prev => [...prev, ...newImages]);
  };

  // Drag Handlers
  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(false); };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files) processFiles(Array.from(e.dataTransfer.files));
  };

  // Image Actions
  const removeImage = (id: string) => setImages(prev => prev.filter(img => img.id !== id));
  const moveImage = (index: number, direction: 'left' | 'right') => {
    if ((direction === 'left' && index === 0) || (direction === 'right' && index === images.length - 1)) return;
    const newImages = [...images];
    const target = direction === 'left' ? index - 1 : index + 1;
    [newImages[index], newImages[target]] = [newImages[target], newImages[index]];
    setImages(newImages);
  };

  const updateImage = (id: string, newUrl: string) => {
      setImages(prev => prev.map(img => {
          if (img.id === id) {
              return { ...img, previewUrl: newUrl }; // Update preview with edited version
          }
          return img;
      }));
  };

  // OCR Logic
  const runOCR = async (img: PDFImage) => {
      setImages(prev => prev.map(p => p.id === img.id ? { ...p, ocrStatus: 'processing' } : p));
      try {
          const result = await processImageForText(img.previewUrl);
          setImages(prev => prev.map(p => p.id === img.id ? { ...p, ocrStatus: 'done', ocrText: result.text } : p));
      } catch (err) {
          setImages(prev => prev.map(p => p.id === img.id ? { ...p, ocrStatus: 'failed' } : p));
      }
  };

  const runAllOCR = async () => {
      const pending = images.filter(i => !i.ocrText);
      await Promise.all(pending.map(runOCR));
  };

  // Generator
  const generatePDF = async () => {
    if (images.length === 0) return;
    setIsGenerating(true);

    try {
      if (settings.includeOCR) {
          await runAllOCR();
      }

      const doc = new jsPDF({
        orientation: settings.orientation as any,
        unit: 'mm',
        format: settings.pageSize
      });

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = settings.margin;

      // Filter valid images to avoid errors
      const validImages = images.filter(img => img.width > 0 && img.height > 0);

      if (settings.layout === 'single') {
        for (let i = 0; i < validImages.length; i++) {
            const img = validImages[i];
            if (i > 0) doc.addPage();
            
            // Layout Math
            const availableWidth = pageWidth - (margin * 2);
            const availableHeight = pageHeight - (margin * 2);
            let imgWidth = availableWidth;
            let imgHeight = (img.height / img.width) * imgWidth;
            
            if (imgHeight > availableHeight) {
                imgHeight = availableHeight;
                imgWidth = (img.width / img.height) * imgHeight;
            }
            
            const x = (pageWidth - imgWidth) / 2;
            const y = (pageHeight - imgHeight) / 2;

            doc.addImage(img.previewUrl, 'JPEG', x, y, imgWidth, imgHeight, undefined, 'FAST');

            // Append OCR Text (Invisible trick or new page)
            if (img.ocrText) {
                doc.addPage();
                doc.setFontSize(10);
                doc.text(`Extracted Text - Page ${i + 1}`, margin, margin);
                const splitText = doc.splitTextToSize(img.ocrText, pageWidth - (margin * 2));
                doc.text(splitText, margin, margin + 10);
            }
        }
      } else if (settings.layout === 'grid-2x2') {
         // Grid Logic
         const itemsPerPage = 4;
         for (let i = 0; i < validImages.length; i += itemsPerPage) {
             if (i > 0) doc.addPage();
             
             const chunk = validImages.slice(i, i + itemsPerPage);
             const cellWidth = pageWidth / 2;
             const cellHeight = pageHeight / 2;
             
             chunk.forEach((img, idx) => {
                 const col = idx % 2;
                 const row = Math.floor(idx / 2);
                 const x = col * cellWidth + margin/2;
                 const y = row * cellHeight + margin/2;
                 const w = cellWidth - margin;
                 const h = cellHeight - margin;
                 
                 doc.addImage(img.previewUrl, 'JPEG', x, y, w, h, undefined, 'FAST');
             });
         }
      }

      doc.save(`scanned_document_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (error) {
      console.error("PDF Generation failed", error);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="h-full flex flex-col space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-gray-100 dark:border-white/5 pb-6">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate(-1)}
            className="rounded-full hover:bg-gray-100 dark:hover:bg-white/10"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
             <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100 flex items-center gap-2">
               <FileText className="w-6 h-6 text-indigo-600" />
               PDF Pro Converter
             </h1>
             <p className="text-xs text-muted-foreground">Convert, Edit, and OCR scans into professional PDFs.</p>
          </div>
        </div>
        <div className="flex gap-2">
           <Tooltip>
             <TooltipTrigger asChild>
               <Button
                variant="outline"
                onClick={() => navigate('/dashboard/utilities/pdf-converter/create')}
                className="border-slate-200 dark:border-white/10 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-white/5"
              >
                <FileText className="w-4 h-4 mr-2" />
                Create PDF
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-[10px]">Create a PDF from scratch using AI-powered document generation with templates.</p>
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                onClick={generatePDF} 
                disabled={images.length === 0 || isGenerating}
                className="bg-indigo-600 text-white hover:bg-indigo-700 shadow-md"
              >
                {isGenerating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                {isGenerating ? 'Processing...' : 'Download PDF'}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-[10px]">{images.length === 0 ? 'Upload images first to generate a PDF' : `Generate and download a PDF with ${images.length} image${images.length !== 1 ? 's' : ''}`}</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-6 h-full min-h-0">
        <div className="lg:col-span-2 flex flex-col h-full min-h-0 space-y-4">
          <div 
            onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} onClick={() => fileInputRef.current?.click()}
            className={`
              relative p-8 border-2 border-dashed rounded-xl text-center cursor-pointer transition-all duration-300
              flex flex-col items-center justify-center gap-2
              ${isDragging ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 scale-[1.02]' : 'border-gray-200 dark:border-white/10 hover:border-indigo-400 hover:bg-gray-50 dark:hover:bg-white/5'}
            `}
          >
            <input type="file" ref={fileInputRef} className="hidden" accept="image/*" multiple onChange={handleFileSelect} />
            <div className={`p-4 rounded-full ${isDragging ? 'bg-indigo-100' : 'bg-gray-100 dark:bg-white/5'}`}>
               <Upload className={`w-8 h-8 ${isDragging ? 'text-indigo-600' : 'text-gray-400'}`} />
            </div>
            <div>
              <p className="font-semibold text-sm">Click to upload or drag and drop</p>
              <p className="text-xs text-muted-foreground">JPG, PNG, WebP (Max 10MB)</p>
            </div>
            {images.length > 0 && (
              <div className="absolute top-4 right-4">
                 <span className="bg-indigo-100 text-indigo-700 text-[10px] font-bold px-2 py-1 rounded-full">{images.length} File{images.length !== 1 ? 's' : ''}</span>
              </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto min-h-[400px] p-1">
             {images.length === 0 ? (
               <div className="h-full flex items-center justify-center text-muted-foreground text-sm italic">No images uploaded yet.</div>
             ) : (
               <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                 <AnimatePresence mode='popLayout'>
                   {images.map((img, index) => (
                     <motion.div 
                        layout initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }}
                        key={img.id}
                        className="relative group aspect-[3/4] bg-gray-100 dark:bg-black rounded-lg overflow-hidden border border-gray-200 dark:border-white/10 shadow-sm"
                     >
                       <img src={img.previewUrl} alt="preview" className="w-full h-full object-contain" />
                       
                        {/* Overlay */}
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-2">
                           <div className="flex justify-between">
                             <span className="bg-black/50 text-white text-[10px] font-mono px-1.5 py-0.5 rounded">{index + 1}</span>
                             <Tooltip>
                               <TooltipTrigger asChild>
                                 <button onClick={() => removeImage(img.id)} className="text-white hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                               </TooltipTrigger>
                               <TooltipContent side="left"><p className="text-[10px]">Remove this image from the PDF</p></TooltipContent>
                             </Tooltip>
                           </div>
                           <div className="flex justify-center flex-col items-center gap-2">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button size="sm" variant="secondary" onClick={() => setEditingImageId(img.id)} className="h-7 text-xs bg-white/90 hover:bg-white">
                                    <Edit2 className="w-3 h-3 mr-1" /> Edit
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent><p className="text-[10px]">Edit this image: rotate, crop, adjust brightness, or add watermarks.</p></TooltipContent>
                              </Tooltip>
                              <div className="flex gap-2">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button onClick={() => moveImage(index, 'left')} disabled={index === 0} className="p-1.5 bg-white/10 hover:bg-white/30 rounded text-white disabled:opacity-30"><ArrowLeft className="w-4 h-4" /></button>
                                  </TooltipTrigger>
                                  <TooltipContent><p className="text-[10px]">Move image earlier in the PDF</p></TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button onClick={() => moveImage(index, 'right')} disabled={index === images.length - 1} className="p-1.5 bg-white/10 hover:bg-white/30 rounded text-white disabled:opacity-30"><ArrowRight className="w-4 h-4" /></button>
                                  </TooltipTrigger>
                                  <TooltipContent><p className="text-[10px]">Move image later in the PDF</p></TooltipContent>
                                </Tooltip>
                              </div>
                          </div>
                          {img.ocrText && (
                             <div className="absolute bottom-2 right-2">
                                <div className="bg-green-500 text-white text-[10px] p-1 rounded-full"><FileText className="w-3 h-3" /></div>
                             </div>
                          )}
                       </div>
                     </motion.div>
                   ))}
                 </AnimatePresence>
               </div>
             )}
          </div>
        </div>

        <div className="h-full">
           <PDFSettings settings={settings} onChange={setSettings} />
        </div>
      </div>

      {editingImageId && (
         <ImageEditor 
            isOpen={!!editingImageId} 
            onClose={() => setEditingImageId(null)} 
            imageUrl={images.find(i => i.id === editingImageId)?.previewUrl || ''}
            onSave={(newUrl) => updateImage(editingImageId, newUrl)}
         />
      )}
    </div>
  );
};

export default IndependentPDFConverterView;
